import { Checkbox, Toggle } from '@/components_deprecated'
import { Card } from '@/components_deprecated/Card'
import { GoogleMap } from '@/components_deprecated/GoogleMap'
import { Module, ModuleOptionsValue } from '@/modules/companies'
import { ColumnNames } from '@/modules/dynamic_tables'
import { format } from '@/utils/date-fns'
import { correctLocalDateWithTimezone } from '@/utils/date/localeCorrection'
import {
  useAdmin,
  useBreadcrumbs,
  useCompany,
  useModuleConfig,
  useParam,
  usePermissions,
  useUser,
} from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { Icon, usePrompt } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import {
  addDays,
  getDaysInMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { MobileOnlyView } from 'react-device-detect'
import { useNavigate } from 'react-router'
import styled, { useTheme } from 'styled-components'
import type { GetAllDriverActivitiesAggregated, TypeChart } from '..'
import {
  enforceHourMinuteString,
  generateActivityMapColors,
  optimizeActivities,
} from '..'
import { getResolution } from '../../issues/consts'
import { MainInfo, Map, Schedule, TripInfo } from '../components/detail'
import { StandardSchedule } from '../components/detail/StandardSchedule'
import { AllActivitiesSumTable } from '../components/overview/AllActivitiesSumTable'
import { CheckmarkDiv } from '../components/overview/CheckmarkDiv'
import { DietTextAndAction } from '../components/overview/DietTextAndAction'
import type { DriverDietForm } from '../components/overview/DriverDietPrompt'
import { DriverDietPrompt } from '../components/overview/DriverDietPrompt'
import { SalaryDay } from '../components/overview/SalaryDayCell'
import type { SalaryDayForm } from '../components/overview/SalaryDayPrompt'
import { SalaryDayPrompt } from '../components/overview/SalaryDayPrompt'
import { useActivityApprovedHandlers, useDriverQueries } from '../hooks'
import { useDietDayMutations, useSalaryDayMutations } from '../mutations.hooks'
import type { VehicleColorDictionary } from '../types'
import { getNodes } from '@/utils/graphql'

const TableWrapper = styled.div`
  margin-bottom: 1rem;
`

const StyledMap = styled(Map)``

const Wrapper = styled.div`
  ${props => props.theme.layout.default};

  header {
    margin-bottom: 0.5rem;

    h2 {
      margin: 0 0 0 0.5rem;
    }
  }

  ${StyledMap} {
    min-width: 500px;
  }
`
const Content = styled(Card)`
  display: grid;
  grid-template-columns: 2fr minmax(300px, 1fr);
  grid-template-rows: 150px 380px auto;
  grid-template-areas:
    'main-info map'
    'trip-info map'
    'chart-type chart-type'
    'schedule  schedule';
  padding: 0;

  ${props => props.theme.media.mobile} {
    grid-template-columns: 100%;
    grid-template-rows: 280px auto;
    grid-template-areas:
      'map'
      'main-info'
      'trip-info'
      'chart-type'
      'schedule';

    overflow: hidden;
  }
`

const ChartTypeWrapper = styled.div`
  grid-area: chart-type;
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  gap: 1rem;
  margin: 0.5rem 0;

  > div {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 1rem;
  }
`

const ActionBarWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  border-radius: ${props => props.theme.standardBorderRadius};
  margin-bottom: 1rem;

  ${props => props.theme.media.mobile} {
    flex-direction: column;
    gap: 1rem;
  }
`

const ActionBarCommon = styled.div`
  height: 44px;
`

export const Navigation = styled(ActionBarCommon)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: ${props => props.theme.standardBorderRadius};
  background-color: ${props => props.theme.colors.white};
  font-size: 1.2rem;
  font-weight: 600;
  height: 44px;
`

export const PrevButton = styled(ActionBarCommon)`
  background-color: ${props => props.theme.colors.white};
  padding: 1rem;
  border-radius: ${props => props.theme.standardBorderRadius} 0 0
    ${props => props.theme.standardBorderRadius};

  width: 38px;

  &:hover {
    cursor: pointer;
    background-color: ${props => props.theme.colors.gray9};
  }
`

export const DateWrapper = styled(ActionBarCommon)`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  height: 44px;
  border-left: 1px solid ${props => props.theme.colors.gray9};
  border-right: 1px solid ${props => props.theme.colors.gray9};

  ${props => props.theme.media.mobile} {
    min-width: 75vw;
  }
`

export const NextButton = styled(ActionBarCommon)`
  background-color: ${props => props.theme.colors.white};
  padding: 1rem;
  border-radius: 0 ${props => props.theme.standardBorderRadius}
    ${props => props.theme.standardBorderRadius} 0;

  width: 38px;

  &:hover {
    cursor: pointer;
    background-color: ${props => props.theme.colors.gray9};
  }
`

const TextAndAction = styled(ActionBarCommon)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem;
  border-radius: ${props => props.theme.standardBorderRadius};
  background-color: ${props => props.theme.colors.white};

  & > *:first-child {
    border-right: 1px solid ${props => props.theme.colors.gray9};
    margin-right: 1rem;
    padding-right: 1rem;
  }

  & > *:last-child {
    cursor: pointer;
  }
  ${props => props.theme.media.mobile} {
    width: 100%;
  }
`

const TextAndActionGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  border-radius: ${props => props.theme.standardBorderRadius};
  gap: 1rem;

  ${props => props.theme.media.mobile} {
    width: 100%;
    justify-content: space-between;
  }
`

const ScheduleWrapper = styled.div<{ isPrint: boolean }>`
  position: relative;
  grid-area: schedule;
  overflow: hidden;
  max-height: ${props => (props.isPrint ? '362px' : 'auto')};
`

const PdfOverlay = styled.div<{ isVisible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(1rem);
  display: ${props => (props.isVisible ? 'flex' : 'none')};
  justify-content: center;
  align-items: center;
  z-index: 1000;
`

const LoaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  background: white;
  padding: 2rem;
  border-radius: ${props => props.theme.standardBorderRadius};
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  min-width: 200px;
`

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${props => props.theme.colors.gray8};
  border-top: 4px solid ${props => props.theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`

// TODO a lot of unused stuff in here

interface ActivitiesDetailProps {}

export const ActivitiesDetail: React.FC<ActivitiesDetailProps> = () => {
  const translations = useTranslate({
    loading: 'common.loading',
    drivingData: 'common.driving-data',
    diet: 'common.diet',
    salaryDay: 'common.salary-day',
    showSums: 'common.show-sums',

    showWeekByHour: 'pdf.show-week-by-hour',
    printPdf: 'pdf.print-pdf',
    loadingPdf: 'pdf.loading-print',
    previousStyle: 'chart.previous-style',
    oldDesign: 'common.old-design',
  })
  // TODO remove?
  // const { chartOptions } = useDateOptions()
  const userId = useParam('userId')
  const dateString = useParam('date')
  const scheduleRef = useRef<HTMLDivElement>(null)
  const [type] = useState<TypeChart>('day')

  const company = useCompany()

  const navigate = useNavigate()

  const { hasPermissions } = usePermissions()
  const me = useUser()
  const admin = useAdmin()
  const theme = useTheme()
  const addPrompt = usePrompt()

  const [typePrint] = useState<TypeChart>('day')
  const startHour = 0
  const [fullWeek, setFullWeek] = useState(false)
  const [standardChart, setStandardChart] = useState(false)
  const isLoading = false

  const isMe = userId === me.id
  const canCheck =
    (isMe &&
      hasPermissions(PERMISSIONS.activities.approve.driveractivity, true)) ||
    (!isMe &&
      hasPermissions(PERMISSIONS.activities.approve.othersdriveractivity, true))
  const { getModuleOptionStatus } = useModuleConfig(Module.ACTIVITIES)
  const hasReceiptFunctionality = getModuleOptionStatus(
    ModuleOptionsValue.ACTIVITIES_RECEIPT_FUNCTIONALITY
  )
  const simpleDietViewActive = getModuleOptionStatus(
    ModuleOptionsValue.ACTIVITIES_SIMPLE_DIET_OPTION
  )
  const commentMandatory = getModuleOptionStatus(
    ModuleOptionsValue.ACTIVITIES_DIET_COMMENT_MANDATORY
  )

  const canSeeReceipt = (admin || userId === me.id) && hasReceiptFunctionality

  const { handler: approveHandler } = useActivityApprovedHandlers()
  const paramDate = new Date(dateString)
  const { setOverride, setOverrides, overrides } = useBreadcrumbs()

  // We want to query on 00:00 related to our timezone
  const tempDate = startOfDay(paramDate)

  const dietMutations = useDietDayMutations()
  const salaryMutations = useSalaryDayMutations()

  const {
    activityLoading,
    activityData,
    driverDataAggregated,
    issuesData,
    infringementsData,
    specificConditionsData,
    data,
    columnData,
  } = useDriverQueries(userId, type, tempDate, dateString)

  useEffect(() => {
    if (
      !activityData?.driverActivityDayReport &&
      overrides[userId] !== translations.loading
    )
      setOverride(userId, translations.loading)
    else if (
      activityData?.driverActivityDayReport &&
      overrides[userId] === translations.loading
    ) {
      const { user } = activityData.driverActivityDayReport
      setOverrides({
        [user.id]: user.fullName,
        [dateString]: format(new Date(dateString), 'PP'),
      })
    }
  }, [
    setOverride,
    setOverrides,
    activityData,
    translations.loading,
    userId,
    dateString,
    overrides,
  ])

  const user = activityData?.driverActivityDayReport?.user ?? null
  const report = activityData?.driverActivityDayReport ?? null
  const issues = getNodes(issuesData?.allIssues)
  const infringements = getNodes(infringementsData?.allInfringements)
  const specificConditions = getNodes(
    specificConditionsData?.allSpecificConditions
  )
  const vehicleLocations = report?.drivenCoordinates ?? []
  const columns = getColumns(canSeeReceipt)

  const date = startOfDay(
    correctLocalDateWithTimezone(paramDate, report?.timezoneData)
  )
  const currentData = date
  // We want the color to be consistent for each vehicle. Vehicle Vin is used as a [key: color]
  const vehicleColors = useMemo<VehicleColorDictionary>(() => {
    const vehicleObject: VehicleColorDictionary = {}
    const vehicleList =
      report?.drivenCoordinates
        .map(drivenCoordinate => drivenCoordinate.vehicleVin ?? 'Unknown')
        .filter(
          (value, index, vehicles) => vehicles.indexOf(value) === index
        ) ?? []
    const vehicleColorList = generateActivityMapColors(vehicleList.length)

    vehicleList.forEach((vehicle, idx) => {
      vehicleObject[vehicle] = vehicleColorList[idx] ?? '#0088FF'
    })

    return vehicleObject
  }, [report?.drivenCoordinates])

  const optimizedActivities = optimizeActivities(
    data?.allFrontendDriverActivities.map(el => ({
      ...el,
      vehicleVin: el.vehicleVin ?? 'Unknown',
      vehicleRegistrationNumber: el.vehicleRegistrationNumber ?? 'Unknown',
    })) ?? []
  )

  const hasDietColumn = columns?.some(
    column => column.name === ColumnNames.DIET
  )
  const hasSalaryColumn = columns?.some(
    column => column.name === ColumnNames.SALARY_DAY
  )

  const form = useForm({
    values: {
      showSums: false,
    },
    config: {
      storage: {
        storeFormState: true,
        storeFormStateName: 'activities-detail-controls',
        retrieveFormStateOnMount: true,
      },
    },
  })

  function getColumns(withReceipt: boolean) {
    const columns = columnData?.allDynamicColumns.edges
      .map(edge => edge.node)
      .filter(node => node.enabled)
    if (
      !withReceipt &&
      columns?.some(column => column.name === ColumnNames.APPROVED)
    ) {
      return columns.filter(node => !(node.name === ColumnNames.APPROVED))
    } else return columns
  }

  async function handleAddDriverDiet() {
    const { data } = await addPrompt<DriverDietForm | null>(resolve => (
      <DriverDietPrompt
        initialData={{
          userId: userId,
          salaryCodeId: report?.diet?.salaryCode?.id ?? null,
          partOfDay: report?.diet?.partOfDay ?? 1,
          comment: report?.diet?.comment ?? '',
        }}
        simpleView={simpleDietViewActive}
        commentMandatory={commentMandatory}
        edit={!!report?.diet}
        onSubmit={resolve}
      />
    ))
    if (data === null) return

    void dietMutations.updateOrCreateDriverDiet({
      variables: {
        input: {
          ...data,
          date: format(date, 'yyyy-MM-dd'),
        },
      },
    })
  }

  async function handleAddSalaryDay() {
    const { data } = await addPrompt<SalaryDayForm | null>(resolve => (
      <SalaryDayPrompt
        initialData={{
          userId: userId,
          partOfDay: report?.salaryDay?.partOfDay ?? 1,
          salaryCodeId: report?.salaryDay?.salaryCode?.id ?? null,
        }}
        onSubmit={resolve}
      />
    ))
    if (data === null || !data.salaryCodeId) return

    void salaryMutations.updateOrCreateSalaryDay({
      variables: {
        input: {
          date: format(date, 'yyyy-MM-dd'),
          userId: userId,
          partOfDay: data.partOfDay,
          salaryCode: data.salaryCodeId,
        },
      },
    })
  }

  function handleOnClickApprove() {
    if (!canCheck) return
    return () => {
      return approveHandler.day(userId, !report?.approved, date)
    }
  }

  const daysCount =
    type === 'week'
      ? 7
      : type === 'month'
      ? getDaysInMonth(currentData)
      : undefined

  const startDate =
    type === 'week' && typePrint !== 'day'
      ? startOfWeek(currentData, { weekStartsOn: 1 })
      : type === 'month' || typePrint === 'month'
      ? startOfMonth(currentData)
      : undefined

  const startHourValue =
    isLoading && typePrint === 'month' ? startHour : undefined
  const resolution = isLoading
    ? getResolution(currentData)[type === 'week' ? 'week' : typePrint]
    : undefined
  const scheduleDate = isLoading ? currentData : date
  const variantValue = isLoading ? typePrint : fullWeek ? 'month' : type
  const infoTooltipValues = report?.registrationNumbers ?? []

  return (
    <Wrapper>
      <MobileOnlyView>
        <header>
          <h2>{translations.drivingData}</h2>
        </header>
      </MobileOnlyView>

      <ActionBarWrapper>
        <Navigation>
          <PrevButton
            onClick={() =>
              void navigate(
                `/activities/${userId}/${format(
                  addDays(date, -1),
                  'yyyy-MM-dd'
                )}`
              )
            }
          >
            <Icon icon="chevron-left" />
          </PrevButton>
          <DateWrapper>{format(date, 'PP')}</DateWrapper>
          <NextButton
            onClick={() =>
              void navigate(
                `/activities/${userId}/${format(
                  addDays(date, 1),
                  'yyyy-MM-dd'
                )}`
              )
            }
          >
            <Icon icon="chevron-right" />
          </NextButton>
        </Navigation>
        {company.shortName !== 'litratermo' && (
          <TextAndActionGroup>
            <TextAndAction>
              <div>{translations.showSums}</div>
              <Checkbox
                checked={form.formValues.showSums}
                onChange={form.formChangeHandler('showSums')}
                boxSize="28px"
              />
            </TextAndAction>
            {report && hasDietColumn && (
              <DietTextAndAction
                diet={report.diet}
                canEdit
                onClick={handleAddDriverDiet}
              />
            )}

            {report && hasSalaryColumn && (
              <TextAndAction>
                <div>{translations.salaryDay}</div>
                <SalaryDay
                  report={report}
                  canEdit={canCheck}
                  onClick={handleAddSalaryDay}
                />
              </TextAndAction>
            )}

            {canSeeReceipt && (
              <CheckmarkDiv
                checked={report?.approved ?? false}
                onClick={handleOnClickApprove()}
              />
            )}
          </TextAndActionGroup>
        )}
      </ActionBarWrapper>

      {form.formValues.showSums && company.shortName !== 'litratermo' && (
        <TableWrapper>
          <AllActivitiesSumTable report={report} />
        </TableWrapper>
      )}

      <Content>
        <MainInfo
          user={user}
          date={date}
          registrationNumbers={report?.registrationNumbers ?? []}
          loading={activityLoading}
        />
        <TripInfo
          startTime={report?.startTime}
          endTime={report?.endTime}
          startLocation={report?.dayStartLocation}
          endLocation={report?.dayEndLocation}
          timeWorkTotal={enforceHourMinuteString(
            driverDataAggregated?.allDriverActivitiesAggregated?.timeWorkTotal
          )}
          distance={report?.distance}
          fuelConsumption={report?.fuelUsage}
          driverGrade={report?.driverGrade}
          drivingGrade={report?.drivingGrade}
        />
        {activityLoading && vehicleLocations.length === 0 ? (
          <GoogleMap
            mapContainerStyle={{
              borderTopRightRadius: theme.sizes.defaultBorderRadius,
            }}
            options={{
              streetViewControl: false,
              zoomControl: false,
              mapTypeControl: false,
            }}
          />
        ) : (
          <StyledMap
            pathColors={vehicleColors}
            vehicleLocations={vehicleLocations}
          />
        )}

        <ChartTypeWrapper>
          <div>
            {/* TODO remove?
             {!isLoading && false && (
              <Select
                value={type}
                onChange={option => {
                  if (option) {
                    setType(option)
                    setTypePrint(option)
                    setStandardChart(false)
                    setFullWeek(false)
                  } else {
                    setTypePrint('day')
                    setType('day')
                  }
                }}
                options={chartOptions}
                width="200px"
              />
            )} */}
          </div>
          <div>
            {type === 'week' && (
              <TextAndAction>
                <div>{translations.showWeekByHour}</div>
                <Toggle value={fullWeek} onChange={setFullWeek} />
              </TextAndAction>
            )}
            {type === 'day' && (
              <TextAndAction>
                <div>{translations.oldDesign}</div>
                <Toggle
                  offIconProps={{ icon: 'times', color: 'red' }}
                  onIconProps={{ icon: 'check', color: 'green' }}
                  value={standardChart}
                  onChange={setStandardChart}
                />
              </TextAndAction>
            )}
          </div>
        </ChartTypeWrapper>
        <ScheduleWrapper isPrint={isLoading}>
          {standardChart ? (
            <StandardSchedule
              activities={optimizedActivities}
              issues={issues}
              specificConditions={specificConditions}
              report={report}
              date={scheduleDate}
              vehicleColors={vehicleColors}
            />
          ) : (
            <Schedule
              times={
                driverDataAggregated?.allDriverActivitiesAggregated ??
                ({} as GetAllDriverActivitiesAggregated['allDriverActivitiesAggregated'])
              }
              fullWeek={fullWeek}
              customDaysCount={daysCount}
              customStartDate={startDate}
              initialStartHour={startHourValue}
              key={`${type}-${currentData.getTime()}-${isLoading}-${daysCount}-${fullWeek}`}
              initialResolution={resolution}
              activities={optimizedActivities}
              issues={issues}
              infringements={infringements}
              specificConditions={specificConditions}
              report={report}
              date={scheduleDate}
              variant={variantValue}
              infoTooltip={infoTooltipValues}
              ref={scheduleRef}
              isPrint={isLoading}
              isMonthBigPrint={isLoading && type === 'month'}
              isOverview={isLoading ? false : undefined}
            />
          )}

          <PdfOverlay
            id="pdf-overlay"
            className="pdf-overlay"
            isVisible={isLoading}
          >
            <LoaderContainer>
              <Spinner />
              <div>{translations.loadingPdf}</div>
            </LoaderContainer>
          </PdfOverlay>
        </ScheduleWrapper>
      </Content>
    </Wrapper>
  )
}
